import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { ui } from '../components/adminUi';
import Icon from '../components/Icon';

const FIELD_TYPES = [
  { value: 'text',     label: 'Matn' },
  { value: 'textarea', label: "Ko'p qatorli matn" },
  { value: 'number',   label: 'Raqam' },
  { value: 'image',    label: 'Rasm' },
  { value: 'select',   label: 'Tanlov (variantlar)' },
];

const TYPE_LABELS = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label]));

function emptyForm() {
  return { label: '', type: 'text', required: false, sortOrder: 0, options: [] };
}

export default function CategoryFieldsPage() {
  const { categoryId } = useParams();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [fields, setFields] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // modal: null | 'add' | field object (edit)
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [optionInput, setOptionInput] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const optionInputRef = useRef(null);

  const load = useCallback(async () => {
    setFetchError('');
    try {
      const [catRes, fieldsRes] = await Promise.all([
        apiFetch(`/api/admin/service-categories/${categoryId}`, auth.token),
        apiFetch(`/api/admin/service-categories/${categoryId}/fields`, auth.token),
      ]);

      if (catRes.status === 404) {
        setFetchError('Kategoriya topilmadi');
        setPageLoading(false);
        return;
      }
      if (!catRes.ok) throw new Error();

      const catData = await catRes.json();
      setCategory(catData);

      if (fieldsRes.ok) {
        const fieldsData = await fieldsRes.json();
        setFields(Array.isArray(fieldsData) ? fieldsData : []);
      }
    } catch {
      setFetchError("Ma'lumotlarni yuklashda xatolik. Sahifani yangilang.");
    } finally {
      setPageLoading(false);
    }
  }, [categoryId, auth.token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(emptyForm());
    setOptionInput('');
    setFormError('');
    setModal('add');
  };

  const openEdit = (field) => {
    setForm({
      label: field.label,
      type: field.type,
      required: field.required,
      sortOrder: field.sortOrder,
      options: Array.isArray(field.options) ? [...field.options] : [],
    });
    setOptionInput('');
    setFormError('');
    setModal(field);
  };

  const closeModal = () => setModal(null);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const addOption = () => {
    const val = optionInput.trim();
    if (!val) return;
    if (form.options.includes(val)) {
      setFormError(`"${val}" varianti allaqachon mavjud`);
      return;
    }
    setForm((f) => ({ ...f, options: [...f.options, val] }));
    setOptionInput('');
    setFormError('');
    optionInputRef.current?.focus();
  };

  const removeOption = (idx) =>
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setFormError('');

    if (!form.label.trim()) {
      setFormError('Maydon nomi (label) majburiy');
      return;
    }
    if (form.type === 'select' && form.options.length === 0) {
      setFormError("Tanlov turi uchun kamida bitta variant kiritish majburiy");
      return;
    }

    setSaving(true);
    try {
      const isEdit = modal !== 'add';
      const path = isEdit
        ? `/api/admin/service-categories/${categoryId}/fields/${modal.id}`
        : `/api/admin/service-categories/${categoryId}/fields`;

      const body = {
        label: form.label.trim(),
        type: form.type,
        required: form.required,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (form.type === 'select') {
        body.options = form.options;
      }

      const res = await apiFetch(path, auth.token, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFormError(d.message || 'Saqlashda xatolik yuz berdi');
        return;
      }
      closeModal();
      await load();
    } catch {
      setFormError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(
        `/api/admin/service-categories/${categoryId}/fields/${deleteTarget.id}`,
        auth.token,
        { method: 'DELETE' }
      );
      setDeleteTarget(null);
      await load();
    } catch {} finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <button className="th-crumb" style={s.breadLink} onClick={() => navigate('/admin/service-categories')}>
          Xizmat kategoriyalari
        </button>
        <span style={s.breadSep}>›</span>
        <span style={s.breadCurrent}>
          {category ? category.name : '...'}
        </span>
      </div>

      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>
            {category ? `"${category.name}" xususiyatlari` : 'Xususiyatlar'}
          </h1>
          <p style={s.subtitle}>Kategoriyaning dinamik forma maydonlarini boshqaring</p>
        </div>
        <button style={s.addBtn} onClick={openAdd} disabled={pageLoading}>
          + Maydon qo'shish
        </button>
      </div>

      {fetchError && <div style={s.errorBanner}>{fetchError}</div>}

      {pageLoading ? (
        <div style={s.centerMsg}>Yuklanmoqda...</div>
      ) : fields.length === 0 && !fetchError ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <Icon name="inbox" size={48} color="var(--color-primary)" strokeWidth={1.75} />
          </div>
          <p style={s.emptyText}>Hali xususiyat qo'shilmagan</p>
          <p style={s.emptyHint}>
            "Maydon qo'shish" tugmasi orqali birinchi xususiyatni qo'shing.
          </p>
        </div>
      ) : !fetchError && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '40px' }}>#</th>
                <th style={s.th}>Nomi (label)</th>
                <th style={s.th}>Turi</th>
                <th style={{ ...s.th, width: '110px', textAlign: 'center' }}>Majburiy</th>
                <th style={{ ...s.th, width: '90px', textAlign: 'center' }}>Tartib</th>
                <th style={{ ...s.th, width: '180px', textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => (
                <tr key={field.id} style={s.tr}>
                  <td style={{ ...s.td, color: 'var(--color-text-muted)' }}>{i + 1}</td>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--color-text)' }}>
                    {field.label}
                    {field.type === 'select' && field.options && field.options.length > 0 && (
                      <div style={s.optionChips}>
                        {field.options.map((opt) => (
                          <span key={opt} style={s.chip}>{opt}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <span style={s.typeBadge}>{TYPE_LABELS[field.type] || field.type}</span>
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    {field.required ? (
                      <span style={s.reqYes}>Ha</span>
                    ) : (
                      <span style={s.reqNo}>Yo'q</span>
                    )}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    {field.sortOrder}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <div style={s.actions}>
                      <button style={s.editBtn} onClick={() => openEdit(field)}>Tahrir</button>
                      <button style={s.delBtn} onClick={() => setDeleteTarget(field)}>O'chirish</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {modal !== null && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>
              {modal === 'add' ? 'Yangi maydon' : 'Maydonni tahrirlash'}
            </h2>

            {/* label */}
            <div style={s.fld}>
              <label style={s.lbl}>
                Maydon nomi (label) <span style={s.req}>*</span>
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setField('label', e.target.value)}
                style={s.input}
                placeholder="Masalan: Xona soni"
                autoFocus
                disabled={saving}
              />
            </div>

            {/* type */}
            <div style={s.fld}>
              <label style={s.lbl}>
                Turi <span style={s.req}>*</span>
              </label>
              <select
                value={form.type}
                onChange={(e) => {
                  setField('type', e.target.value);
                  if (e.target.value !== 'select') {
                    setFormError('');
                  }
                }}
                style={s.select}
                disabled={saving}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* options — only for select */}
            {form.type === 'select' && (
              <div style={s.fld}>
                <label style={s.lbl}>
                  Variantlar <span style={s.req}>*</span>
                </label>
                <div style={s.optionsList}>
                  {form.options.map((opt, idx) => (
                    <div key={idx} style={s.optionRow}>
                      <span style={s.optionText}>{opt}</span>
                      <button
                        type="button"
                        style={s.optionRemove}
                        onClick={() => removeOption(idx)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {form.options.length === 0 && (
                    <p style={s.optionHint}>Hali variant qo'shilmagan</p>
                  )}
                </div>
                <div style={s.optionAddRow}>
                  <input
                    ref={optionInputRef}
                    type="text"
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addOption(); }
                    }}
                    style={{ ...s.input, flex: 1, marginBottom: 0 }}
                    placeholder="Variant matni..."
                    disabled={saving}
                  />
                  <button
                    type="button"
                    style={s.optionAddBtn}
                    onClick={addOption}
                    disabled={saving || !optionInput.trim()}
                  >
                    + Qo'shish
                  </button>
                </div>
              </div>
            )}

            {/* required */}
            <div style={{ ...s.fld, ...s.checkboxRow }}>
              <input
                id="field-required"
                type="checkbox"
                checked={form.required}
                onChange={(e) => setField('required', e.target.checked)}
                style={s.checkbox}
                disabled={saving}
              />
              <label htmlFor="field-required" style={s.checkboxLabel}>
                Majburiy maydon
              </label>
            </div>

            {/* sortOrder */}
            <div style={s.fld}>
              <label style={s.lbl}>Tartib raqami (sortOrder)</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setField('sortOrder', e.target.value)}
                style={{ ...s.input, width: '120px' }}
                min="0"
                disabled={saving}
              />
            </div>

            {formError && <div style={s.formError}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal} disabled={saving}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.saveBtn, ...(saving ? s.btnDisabled : {}) }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modalCard, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>O'chirishni tasdiqlang</h2>
            <p style={s.confirmText}>
              <strong>"{deleteTarget.label}"</strong> maydonini o'chirishni xohlaysizmi?
              Bu amalni qaytarib bo'lmaydi.
            </p>
            <div style={s.modalActions}>
              <button
                style={s.cancelBtn}
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Bekor qilish
              </button>
              <button
                style={{ ...s.deleteBtn, ...(deleting ? s.btnDisabled : {}) }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "O'chirilmoqda..." : "O'chirish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const s = {
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  breadLink: {
    background: 'none',
    border: 'none',
    color: 'var(--color-text-muted)',
    cursor: 'pointer',
    fontSize: '14px',
    padding: 0,
    fontFamily: 'inherit',
  },
  breadSep: {
    color: 'var(--color-text-muted)',
  },
  breadCurrent: {
    color: 'var(--color-text)',
    fontWeight: '500',
  },
  pageHeader: ui.pageHeader,
  title: ui.title,
  subtitle: ui.subtitle,
  addBtn: ui.addBtn,
  errorBanner: ui.errorBanner,
  centerMsg: ui.centerMsg,
  empty: ui.empty,
  emptyIcon: ui.emptyIcon,
  emptyText: ui.emptyText,
  emptyHint: ui.emptyHint,
  tableWrap: ui.tableWrap,
  table: ui.table,
  th: ui.th,
  tr: ui.tr,
  td: ui.td,
  typeBadge: { ...ui.badge, background: 'var(--color-surface-alt)', color: 'var(--color-text)' },
  reqYes: { ...ui.badge, background: 'var(--color-success)', color: 'var(--color-on-dark)' },
  reqNo: { ...ui.badge, background: 'var(--color-surface-alt)', color: 'var(--color-text)' },
  optionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '6px',
  },
  chip: { ...ui.badge, background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)' },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  editBtn: { ...ui.btnSm, ...ui.btnBlue },
  delBtn: { ...ui.btnSm, ...ui.btnRed },
  overlay: ui.overlay,
  modalCard: ui.modalCard,
  modalTitle: ui.modalTitle,
  fld: { marginBottom: '16px' },
  lbl: ui.label,
  req: ui.req,
  input: ui.input,
  select: ui.select,
  optionsList: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-input)',
    padding: '8px',
    marginBottom: '8px',
    minHeight: '40px',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px',
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-btn)',
    marginBottom: '4px',
    border: '1px solid var(--color-border)',
  },
  optionText: { fontSize: '14px', color: 'var(--color-text)' },
  optionRemove: {
    background: 'none',
    border: 'none',
    color: 'var(--color-danger)',
    fontSize: '18px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 4px',
  },
  optionHint: {
    margin: '4px 0',
    fontSize: '13px',
    color: 'var(--color-text-muted)',
    textAlign: 'center',
  },
  optionAddRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  optionAddBtn: {
    ...ui.btnSm,
    minHeight: '48px',
    padding: '10px 14px',
    background: 'var(--color-dark)',
    color: 'var(--color-on-dark)',
    border: 'none',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: 'var(--color-dark)',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: 'var(--color-text)',
    cursor: 'pointer',
    fontWeight: '500',
  },
  formError: ui.formError,
  modalActions: ui.modalActions,
  cancelBtn: ui.cancelBtn,
  saveBtn: ui.saveBtn,
  deleteBtn: {
    ...ui.saveBtn,
    background: 'var(--color-danger)',
    color: 'var(--color-on-dark)',
  },
  btnDisabled: ui.btnDisabled,
  confirmText: {
    fontSize: '14px',
    color: 'var(--color-text)',
    lineHeight: '1.6',
    margin: '0 0 20px',
  }
};
